/**
 * The semantic icon map (spec M0-F7 §6.5).
 *
 * This is the ONLY module in the repository that imports `@vibe/icons`. Screens ask for
 * `icons.board`, never for `Board`, so replacing a glyph is one edit here and nothing else
 * changes. If a glyph does not exist in `@vibe/icons@4.1.0` an SVG lands in `./custom/` and is
 * registered in the same record — screens never branch on the source.
 *
 * Every name below was verified against the installed `@vibe/icons@4.1.0` export list (276
 * SVGs), which answers open question 101 for the 34 names the spec marked ASSUMED. Three of the
 * spec's "confirmed" names do not exist and are noted inline.
 */
import { Activity, Add, Apps, Archive, Board, BoardPrivate, BoardShareable, BoardTemplate, Bolt, Bookmark, Calendar, Check, Checkbox, Column, Close, Collapse, Comment, Dashboard, Delete, Edit, Doc, DoubleCheck, Download, Drag, Dropdown, DropdownChevronDown, DropdownChevronRight, Duplicate, Email, Emoji, Favorite, Feedback, Filter, Folder, Group, Help, Hide, Home, Inbox, Info, Invite, Item, Keyboard, Lines, Link, Locked, LogOut, LongText, Mention, Menu, MoreActions, MoveArrowRight, NavigationChevronDown, NavigationChevronLeft, NavigationChevronRight, NavigationDoubleChevronLeft, NewTab, Note, Notifications, Numbers, Person, Pin, PinFull, Reply, ScheduledSend, Search, Settings, ShortText, Show, Sort, Status, Subitems, Text, TextFormatting, ThumbsUp, Time, Timeline, Update, Upload, Wand, Work, Workspace, } from '@vibe/icons';
import { Crown, CrownFilled } from './custom/Crown.js';
import { ExpandRight } from './custom/ExpandRight.js';
import { FavoriteFilled } from './custom/FavoriteFilled.js';
/**
 * Semantic name → glyph. Frozen, so a screen cannot mutate the map at runtime (I1).
 *
 * Deviations from the spec's §6.5 table, forced by the installed package:
 *   - `subitem` → `Subitems` (there is no singular `Subitem` export).
 *   - `automation` → `Bolt` (there is no `Automation` export; `Bolt` is the glyph monday's own
 *     automations button uses). `Workflow` exists too and is mapped separately.
 *   - `favoriteFilled` and `expandRight` have no Vibe equivalent and live in `./custom/`.
 */
/**
 * Keeps the key union while erasing the value types. Without it TypeScript writes the inferred
 * type of every Vibe glyph into the emitted `.d.ts` as a path into `node_modules/.pnpm/...`,
 * which is not portable across installs (TS2742).
 */
function defineIcons(map) {
    return Object.freeze(map);
}
export const icons = defineIcons({
    // entities
    board: Board,
    boardPrivate: BoardPrivate,
    boardShareable: BoardShareable,
    boardTemplate: BoardTemplate,
    doc: Doc,
    dashboard: Dashboard,
    folder: Folder,
    workspace: Workspace,
    group: Group,
    item: Item,
    subitem: Subitems,
    // item actions
    convertToItem: Item,
    convertToSubitem: Subitems,
    // columns and board surfaces
    status: Status,
    timeline: Timeline,
    person: Person,
    update: Update,
    /* The update card's own action row (P-029): monday draws `Like` and `Reply` with a
       thumbs-up and a reply arrow, and the composer's formatting control with `TextFormatting`.
       All three exist in `@vibe/icons@4.1.0` and only needed a semantic name. */
    like: ThumbsUp,
    reply: Reply,
    textFormatting: TextFormatting,
    automation: Bolt,
    activity: Activity,
    // shell
    notifications: Notifications,
    bell: Notifications,
    inbox: Inbox,
    /**
     * P-286 · the Update feed's own glyphs, read off `[ui:library/shell-07-inbox.png]`: the tab
     * strip carries an `@`, a bookmark ribbon, a briefcase and a scheduled-send paper plane, and
     * `Mark all as read` carries a double tick. All four exist in `@vibe/icons@4.1.0` and only
     * needed a semantic name, so the feed asks for `icons.mention`, never for `Mention`.
     */
    mention: Mention,
    bookmark: Bookmark,
    account: Work,
    scheduled: ScheduledSend,
    markAllRead: DoubleCheck,
    invite: Invite,
    apps: Apps,
    help: Help,
    /* The ⓘ monday puts next to a pane's own explanation (P-031 quick filters). */
    info: Info,
    home: Home,
    /**
     * M10 · the **Tasks** rail entry (`docs/13-tasks-section-proposal.md`). The Tasks section is
     * ours, not monday's, so there is no monday glyph to match; `Checkbox` is the closest thing in
     * `@vibe/icons@4.1.0` to "a list of things to tick off", and the rail asks for `icons.tasks`
     * rather than for a glyph, so swapping it later is one edit here (I1).
     */
    tasks: Checkbox,
    search: Search,
    menu: Menu,
    /**
     * `@vibe/icons@4.1.0` quirk, measured in round 8c: **`Menu` is the three-dot glyph** (three
     * filled 3 px dots on one row) and **`MoreActions` is a double chevron** pointing down.
     *
     * T-8d audited all 16 `icons.moreActions` call sites in `apps/web`: every one of them is a
     * `⋯` menu trigger on monday — the sidebar rows and the rail's *More*, the board header, the
     * board and table toolbars, the view tab menu, a grid row, a group header, a subitem row, a
     * kanban card, the form view and the notification pane. So the name is re-pointed at the
     * glyph it always claimed to draw, rather than 16 call sites being edited one by one.
     * `doubleChevronDown` keeps Vibe's own `MoreActions` reachable for a "show more" affordance.
     */
    moreActions: Menu,
    doubleChevronDown: MoreActions,
    /** The three horizontal rules of a "list density" control (P-032). */
    lines: Lines,
    /** A speech bubble — the header control monday puts left of a modal's ✕ (P-032, P-029). */
    comment: Comment,
    feedback: Feedback,
    settings: Settings,
    logOut: LogOut,
    shortcuts: Keyboard,
    // generic verbs
    add: Add,
    close: Close,
    filter: Filter,
    sort: Sort,
    hide: Hide,
    show: Show,
    groupBy: Group,
    duplicate: Duplicate,
    edit: Edit,
    delete: Delete,
    archive: Archive,
    favorite: Favorite,
    favoriteFilled: FavoriteFilled,
    /* P-043: the board people dialog's `ownership-button-<userId>`, in its two states. No crown
       exists in `@vibe/icons@4.1.0`, so both live in `./custom/`. */
    crown: Crown,
    crownFilled: CrownFilled,
    link: Link,
    download: Download,
    upload: Upload,
    email: Email,
    emoji: Emoji,
    dragHandle: Drag,
    checkSmall: Check,
    openInNewTab: NewTab,
    moveArrowRight: MoveArrowRight,
    // disclosure and navigation chevrons
    collapse: Collapse,
    collapseLeft: NavigationDoubleChevronLeft,
    expandRight: ExpandRight,
    chevronDown: NavigationChevronDown,
    chevronLeft: NavigationChevronLeft,
    chevronRight: NavigationChevronRight,
    disclosureClosed: DropdownChevronRight,
    disclosureOpen: DropdownChevronDown,
    /* column types — the add-column gallery, the header cell and the filter/hide panes
     * (M1-B5 §6 `ColumnTypeUi.icon`). One name per registered type, so the web registry never
     * imports a glyph itself. */
    columnName: ShortText,
    columnText: Text,
    columnLongText: LongText,
    columnNumbers: Numbers,
    columnDate: Calendar,
    columnStatus: Status,
    columnDropdown: Dropdown,
    columnPeople: Person,
    columnCheckbox: Checkbox,
    columnSubtasks: Subitems,
    columnCreationLog: Time,
    columnLastUpdated: Update,
    columnGeneric: Column,
    /* table chrome (M1-B4 §8.2, §8.11) */
    pin: Pin,
    pinFilled: PinFull,
    description: Note,
    /** monday's `✦ Auto-assign` AI affordance; this product renders it disabled `[P-003]`. */
    aiSparkle: Wand,
    /**
     * The padlock the board-info popover puts on its **Permissions** row when the board is not
     * at *Edit everything* (`[c:prod-board-info-permissions]`, P-041).
     */
    locked: Locked,
});
/** Every semantic name, for the `/__ui` gallery and for the lint rule of I3. */
export const iconNames = Object.freeze(Object.keys(icons));
//# sourceMappingURL=index.js.map